
// Helper function to generate dropdown options from working data
const generateDropdownOptions = (state: MappingState): MappingState['dropdownOptions'] => {
  // Attributes dropdown - only considered and reviewed items
  const attributes = state.working.attributes
    .filter(attr => attr.isConsidered && attr.isReviewed)
    .map(attr => ({
      value: attr.attribute,
      label: attr.attribute
    }));

  // Attribute values dropdown - only considered and reviewed items from flattened data
  const attributeValues = state.flattenedAttributeValues.working
    .filter(attrVal => attrVal.isConsidered && attrVal.isReviewed)
    .map(attrVal => ({
      value: attrVal.attribute_value,
      label: attrVal.attribute_value
    }));

  return {
    attributes,
    attributeValues,
    mappedCategories: state.dropdownOptions.mappedCategories // Keep existing mapped categories
  };
};

// Helper function to deep clone data
const deepClone = <T>(obj: T): T => JSON.parse(JSON.stringify(obj));

// Helper function to add item to Set (compatible with older TypeScript)
const addToSet = (existingSet: Set<string>, newItem: string): Set<string> => {
  const newSet = new Set<string>();
  existingSet.forEach(item => newSet.add(item));
  newSet.add(newItem);
  return newSet;
};

// Capture the freshly-edited category row from `categories` into the
// modifiedCategories overlay so the edit survives the next search / filter /
// pagination reload (which replaces working.categories). Last write per id wins.
const captureCategoryOverlay = (
  overlay: { [id: string]: MappingCategory },
  categories: MappingCategory[],
  id: string
): { [id: string]: MappingCategory } => {
  const cat = categories.find(c => c.id === id);
  return cat ? { ...overlay, [id]: cat } : overlay;
};

// Helper function to check if there are unsaved changes
const hasUnsavedChanges = (state: MappingState): boolean => {
  return (
    state.modifiedIds.attributes.size > 0 ||
    state.modifiedIds.attributeValues.size > 0 ||
    state.modifiedIds.categories.size > 0
  );
};

// Helper function to flatten attribute values from nested structure
const flattenAttributeValues = (attributes: MappingAttribute[]): MappingAttributeValue[] => {
  const flattened: MappingAttributeValue[] = [];
  attributes.forEach(attr => {
    if (attr.attributeValues && attr.attributeValues.length > 0) {
      flattened.push(...attr.attributeValues);
    }
  });
  return flattened;
};

// Phase 2: AV rows are unique per (mapped_attribute, attribute_value) pair —
// the same incoming value string (e.g. "Red", "1") commonly exists under
// multiple mapped attributes. Every identity check on flattened AV rows must
// use this composite key: keying on attribute_value alone cross-links rows
// between attributes (an edit under one mapped attribute clobbers/hides
// another attribute's row with the same value string).
export const avRowKey = (
  mappedAttribute: string | null | undefined,
  attributeValue: string | null | undefined
): string => `${mappedAttribute ?? ''}\u001F${attributeValue ?? ''}`;

// Helper function to update nested attribute values and return updated attributes array.
// `parentAttribute` (the mapped attribute name) scopes the update so a value
// string shared across attributes doesn't update every parent's copy.
const updateNestedAttributeValue = (
  attributes: MappingAttribute[],
  attributeValueId: string,
  changes: Partial<MappingAttributeValue>,
  parentAttribute?: string
): MappingAttribute[] => {
  return attributes.map(attr => {
    if (parentAttribute && attr.attribute !== parentAttribute && attr.mapped_attribute !== parentAttribute) {
      return attr;
    }
    return {
      ...attr,
      attributeValues: attr.attributeValues ? attr.attributeValues.map(attrVal =>
        attrVal.attribute_value === attributeValueId ? { ...attrVal, ...changes } : attrVal
      ) : []
    };
  });
};

// Initial state
export const initialMappingState: MappingState = {
  original: {
    attributes: [],
    categories: [],
  },
  working: {
    attributes: [],
    categories: []
  },
  flattenedAttributeValues: {
    original: [],
    working: []
  },
  modifiedIds: {
    attributes: new Set<string>(),
    attributeValues: new Set<string>(),
    categories: new Set<string>()
  },
  modifiedAttributes: {},
  modifiedCategories: {},
  deletedCategoryIds: [],
  dropdownOptions: {
    attributes: [],
    attributeValues: [],
    mappedCategories: []
  },
  hasUnsavedChanges: false,
  loading: false,
  saving: false,
  pagination: {
    attributes: {
      currentPage: 0,
      hasMore: true,
      loading: false,
      totalItems: 0,
      uniqueMappedCount: 0,
      pendingReviewCount: 0
    },
    attributeValues: {
      currentPage: 0,
      hasMore: true,
      loading: false,
      totalItems: 0,
      uniqueMappedCount: 0,
      pendingReviewCount: 0
    },
    categories: {
      currentPage: 0,
      hasMore: true,
      loading: false,
      totalItems: 0,
      uniqueMappedCount: 0,
      pendingReviewCount: 0
    }
  }
};

export const initialFilterState: FilterState = {
  consider: [],
  review: [],
  attributes: {
    incoming: [],
    mapped: []
  },
  attributeValues: {
    incoming: [],
    mapped: []
  },
  categories: {
    reportingCategory: [],
    mappedCategory: []
  }
};

// Reducer function
export const mappingReducer = (state: MappingState, action: MappingAction): MappingState => {
  switch (action.type) {

    case 'UPDATE_ATTRIBUTE': {
      const { id, changes } = action.payload;

      const updatedChanges = { ...changes };
      // Phase 2: an empty mapped_attributes array means the row is unmapped,
      // so flip isConsidered=false. Same legacy behavior also still honored
      // for the scalar mapped_attribute key.
      const mappedAttrs = (changes as any).mapped_attributes;
      const mappedAttrLegacy = (changes as any).mapped_attribute;
      const isClearedList = Array.isArray(mappedAttrs) && mappedAttrs.length === 0;
      const isClearedScalar = typeof mappedAttrLegacy === 'string' && mappedAttrLegacy.trim() === '';
      if (isClearedList || isClearedScalar) {
        updatedChanges.isConsidered = false;
      }

      // Check if the update contains meaningful changes (not just loading attributeValues)
      // attributeValues is loaded dynamically and shouldn't mark the attribute as modified
      const isOnlyAttributeValuesUpdate = Object.keys(changes).length === 1 && 'attributeValues' in changes;

      const updatedAttributes = state.working.attributes.map(attr =>
        attr.attribute === id ? { ...attr, ...updatedChanges } : attr
      );

      // Persist the edited row into the overlay so it survives the next
      // search/filter/pagination reload (which replaces working.attributes).
      // Merge onto any prior overlay entry for this id so partial edits
      // accumulate but the LAST value per field wins — exactly one entry per
      // attribute. Skip when this is only a lazy attributeValues load.
      let modifiedAttributes = state.modifiedAttributes;
      if (!isOnlyAttributeValuesUpdate) {
        const prior = state.modifiedAttributes[id]
          || state.working.attributes.find(attr => attr.attribute === id);
        modifiedAttributes = {
          ...state.modifiedAttributes,
          [id]: { ...(prior || {}), ...updatedChanges } as MappingAttribute
        };
      }

      const newState = {
        ...state,
        working: {
          ...state.working,
          attributes: updatedAttributes
        },
        modifiedAttributes,
        modifiedIds: {
          ...state.modifiedIds,
          // Only add to modified IDs if this is not just loading attribute values
          attributes: isOnlyAttributeValuesUpdate
            ? state.modifiedIds.attributes
            : addToSet(state.modifiedIds.attributes, id)
        }
      };

      newState.hasUnsavedChanges = hasUnsavedChanges(newState);

      // Refresh dropdown options if consider or review status changed
      if (updatedChanges.isConsidered !== undefined || updatedChanges.isReviewed !== undefined) {
        newState.dropdownOptions = generateDropdownOptions(newState);
      }

      return newState;
    }

    case 'UPDATE_ATTRIBUTE_VALUE': {
      const { id, changes, mappedAttribute } = action.payload;

      const updatedChanges = { ...changes };
      // Phase 2: BE field `mapped_attribute_value` is now list-typed; an
      // empty list means the row is unmapped, so flip isConsidered=false.
      // The legacy scalar branch is also honored for transitional safety.
      const mappedVal = (changes as any).mapped_attribute_value;
      const isClearedList = Array.isArray(mappedVal) && mappedVal.length === 0;
      const isClearedScalar = typeof mappedVal === 'string' && mappedVal.trim() === '';
      if (isClearedList || isClearedScalar) {
        updatedChanges.isConsidered = false;
      }

      // Scope the row match to (mapped_attribute, attribute_value) — the same
      // value string exists under multiple mapped attributes, and an unscoped
      // id would update every one of them. `mappedAttribute` is absent only
      // for legacy callers, where the old value-only match is kept.
      const matchesRow = (attrVal: MappingAttributeValue) =>
        attrVal.attribute_value === id &&
        (!mappedAttribute || attrVal.mapped_attribute === mappedAttribute);

      // Update in nested structure
      const updatedAttributes = updateNestedAttributeValue(state.working.attributes, id, updatedChanges, mappedAttribute);

      // Update flattened structure
      const updatedFlattenedAttributeValues = state.flattenedAttributeValues.working.map(attrVal =>
        matchesRow(attrVal) ? { ...attrVal, ...updatedChanges } : attrVal
      );

      // Track the modification under the composite key. When the caller didn't
      // pass mappedAttribute, derive it from the (unique) matched working row.
      const matchedRow = state.flattenedAttributeValues.working.find(matchesRow);
      const rowKey = avRowKey(mappedAttribute ?? matchedRow?.mapped_attribute, id);

      const newState = {
        ...state,
        working: {
          ...state.working,
          attributes: updatedAttributes
        },
        flattenedAttributeValues: {
          ...state.flattenedAttributeValues,
          working: updatedFlattenedAttributeValues
        },
        modifiedIds: {
          ...state.modifiedIds,
          attributeValues: addToSet(state.modifiedIds.attributeValues, rowKey)
        }
      };

      newState.hasUnsavedChanges = hasUnsavedChanges(newState);

      // Refresh dropdown options if consider or review status changed
      if (updatedChanges.isConsidered !== undefined || updatedChanges.isReviewed !== undefined) {
        newState.dropdownOptions = generateDropdownOptions(newState);
      }

      return newState;
    }

    case 'LOAD_ATTRIBUTE_VALUES_FOR_DROPDOWN': {
      // Load attribute values for a specific attribute (used in category dropdown)
      const { attribute, values } = action.payload;

      const newState = {
        ...state,
        working: {
          ...state.working,
          attributes: state.working.attributes.map(attr => {
            // Match by either incoming or mapped attribute name
            if (attr.attribute === attribute || attr.mapped_attribute === attribute) {
              return {
                ...attr,
                attributeValues: values
              };
            }
            return attr;
          })
        }
      };

      // Regenerate dropdown options since we added attribute values
      newState.dropdownOptions = generateDropdownOptions(newState);

      return newState;
    }

    case 'UPDATE_CATEGORY': {
      const { id, changes } = action.payload;

      const newState = {
        ...state,
        working: {
          ...state.working,
          categories: state.working.categories.map(category => {
            if (category.id === id) {
              const updatedCategory = { ...category, ...changes };

              // Auto-disable if mapped category is empty
              if (changes.mappedcategory_name !== undefined &&
                (!changes.mappedcategory_name || changes.mappedcategory_name.trim() === '')) {
                updatedCategory.isConsidered = false;
              }

              // Auto-disable if attribute exists but attribute value is empty
              // Only check this if we're not currently updating the attribute field
              if (updatedCategory.attribute && updatedCategory.attribute.length > 0) {
                const hasEmptyAttributeValue = updatedCategory.attribute.some(
                  pair => pair.attribute && pair.attribute.trim() !== '' &&
                    (!pair.attributeValue || pair.attributeValue.trim() === '')
                );
                if (hasEmptyAttributeValue) {
                  updatedCategory.isConsidered = false;
                }
              }

              return updatedCategory;
            }
            return category;
          })
        },
        modifiedIds: {
          ...state.modifiedIds,
          categories: addToSet(state.modifiedIds.categories, id)
        }
      };

      newState.modifiedCategories = captureCategoryOverlay(state.modifiedCategories, newState.working.categories, id);
      newState.hasUnsavedChanges = hasUnsavedChanges(newState);
      return newState;
    }

    case 'ADD_ATTRIBUTE_PAIR_TO_CATEGORY': {
      const { categoryId } = action.payload;

      const newPair: AttributeValuePair = {
        attribute: '',
        attributeValue: ''
      };

      const newState = {
        ...state,
        working: {
          ...state.working,
          categories: state.working.categories.map(category =>
            category.id === categoryId
              ? { ...category, attribute: [...category.attribute, newPair] }
              : category
          )
        },
        modifiedIds: {
          ...state.modifiedIds,
          categories: addToSet(state.modifiedIds.categories, categoryId)
        }
      };

      newState.modifiedCategories = captureCategoryOverlay(state.modifiedCategories, newState.working.categories, categoryId);
      newState.hasUnsavedChanges = hasUnsavedChanges(newState);
      return newState;
    }

    case 'REMOVE_ATTRIBUTE_PAIR': {
      const { categoryId, pairId } = action.payload;
      const pairIndex = parseInt(pairId);

      const newState = {
        ...state,
        working: {
          ...state.working,
          categories: state.working.categories.map(category => {
            if (category.id === categoryId) {
              const filteredPairs = category.attribute.filter((_, index) => index !== pairIndex);
              const updatedCategory = {
                ...category,
                attribute: filteredPairs.length > 0 ? filteredPairs : category.attribute
              };

              // Auto-disable if all attribute pairs are removed or empty
              if (updatedCategory.attribute.length === 0 ||
                updatedCategory.attribute.every(pair => !pair.attribute && !pair.attributeValue)) {
                updatedCategory.isConsidered = false;
              }

              return updatedCategory;
            }
            return category;
          })
        },
        modifiedIds: {
          ...state.modifiedIds,
          categories: addToSet(state.modifiedIds.categories, categoryId)
        }
      };

      newState.modifiedCategories = captureCategoryOverlay(state.modifiedCategories, newState.working.categories, categoryId);
      newState.hasUnsavedChanges = hasUnsavedChanges(newState);
      return newState;
    }

    case 'UPDATE_ATTRIBUTE_PAIR': {
      const { categoryId, pairIndex, changes } = action.payload;

      const newState = {
        ...state,
        working: {
          ...state.working,
          categories: state.working.categories.map(category => {
            if (category.id === categoryId) {
              const updatedCategory = {
                ...category,
                attribute: category.attribute.map((pair, index) =>
                  index === pairIndex ? { ...pair, ...changes } : pair
                )
              };

              // Auto-disable if attribute exists but attribute value is empty
              if (updatedCategory.attribute && updatedCategory.attribute.length > 0) {
                const hasEmptyAttributeValue = updatedCategory.attribute.some(
                  pair => pair.attribute && (!pair.attributeValue || pair.attributeValue.trim() === '')
                );
                if (hasEmptyAttributeValue) {
                  updatedCategory.isConsidered = false;
                }
              }

              return updatedCategory;
            }
            return category;
          })
        },
        modifiedIds: {
          ...state.modifiedIds,
          categories: addToSet(state.modifiedIds.categories, categoryId)
        }
      };

      newState.modifiedCategories = captureCategoryOverlay(state.modifiedCategories, newState.working.categories, categoryId);
      newState.hasUnsavedChanges = hasUnsavedChanges(newState);
      return newState;
    }

    case 'REFRESH_DROPDOWN_OPTIONS': {
      return {
        ...state,
        dropdownOptions: generateDropdownOptions(state)
      };
    }

    case 'SAVE_SUCCESS': {
      // After successful save, update original data and clear modifications
      const flattenedOriginal = flattenAttributeValues(state.working.attributes);

      return {
        ...state,
        original: {
          attributes: deepClone(state.working.attributes),
          categories: deepClone(state.working.categories),
        },
        flattenedAttributeValues: {
          ...state.flattenedAttributeValues,
          original: deepClone(flattenedOriginal)
        },
        modifiedIds: {
          attributes: new Set<string>(),
          attributeValues: new Set<string>(),
          categories: new Set<string>()
        },
        modifiedAttributes: {},
        modifiedCategories: {},
        deletedCategoryIds: [],
        hasUnsavedChanges: false,
        saving: false
      };
    }

    case 'DISCARD_CHANGES': {
      // Reset working data to original and clear modifications
      const resetWorkingAttributes = deepClone(state.original.attributes);
      const resetWorkingCategories = deepClone(state.original.categories);
      const resetFlattenedAttributeValues = deepClone(state.flattenedAttributeValues.original);

      const resetWorkingData = {
        attributes: resetWorkingAttributes,
        categories: resetWorkingCategories
      };

      return {
        ...state,
        working: resetWorkingData,
        flattenedAttributeValues: {
          ...state.flattenedAttributeValues,
          working: resetFlattenedAttributeValues
        },
        modifiedIds: {
          attributes: new Set<string>(),
          attributeValues: new Set<string>(),
          categories: new Set<string>()
        },
        modifiedAttributes: {},
        modifiedCategories: {},
        deletedCategoryIds: [],
        hasUnsavedChanges: false,
        dropdownOptions: generateDropdownOptions({
          ...state,
          working: resetWorkingData,
          flattenedAttributeValues: {
            ...state.flattenedAttributeValues,
            working: resetFlattenedAttributeValues
          }
        })
      };
    }

    case 'SET_LOADING': {
      return {
        ...state,
        loading: action.payload
      };
    }

    case 'SET_SAVING': {
      return {
        ...state,
        saving: action.payload
      };
    }

    case 'SET_AV_UNIQUE_COUNT': {
      // The AV "Unique Mapped" count is fetched on its own endpoint, in
      // parallel with the data page (so the ~3s count doesn't block the table).
      // loading=true is dispatched when the fetch starts; the count + loading
      // false when it resolves. Only `count` keys present are applied, so a
      // loading-only dispatch doesn't wipe a prior value.
      const { count, loading } = action.payload;
      return {
        ...state,
        pagination: {
          ...state.pagination,
          attributeValues: {
            ...state.pagination.attributeValues,
            ...(count !== undefined ? { uniqueMappedCount: count } : {}),
            uniqueMappedCountLoading: !!loading
          }
        }
      };
    }

    case 'COPY_CATEGORY': {
      const { category, copiedCategoryId } = action.payload;

      const copiedCategory: MappingCategory = {
        ...category,
        id: copiedCategoryId,
        isConsidered: false,
        isReviewed: false,
        // copy_of_id is sent to the backend so the server-side order keeps
        // the copy directly below its source after the post-save refetch.
        copy_of_id: category.id,
      };

      // Splice the copy directly below the original rather than appending to
      // the end — keeps the row in the user's line of sight while editing.
      const originalIndex = state.working.categories.findIndex(cat => cat.id === category.id);
      const insertIndex = originalIndex === -1
        ? state.working.categories.length
        : originalIndex + 1;
      const nextCategories = [
        ...state.working.categories.slice(0, insertIndex),
        copiedCategory,
        ...state.working.categories.slice(insertIndex),
      ];

      const newState = {
        ...state,
        working: {
          ...state.working,
          categories: nextCategories
        },
        modifiedIds: {
          ...state.modifiedIds,
          categories: addToSet(state.modifiedIds.categories, copiedCategoryId)
        }
      };

      // Persist the copied row (carries copy_of_id) so it survives reloads and
      // is re-injected even when the current page doesn't include it.
      newState.modifiedCategories = captureCategoryOverlay(state.modifiedCategories, newState.working.categories, copiedCategoryId);
      newState.hasUnsavedChanges = hasUnsavedChanges(newState);
      return newState;
    }

    case 'DELETE_CATEGORY': {
      const { categoryId } = action.payload;

      const existsInOriginal = state.original.categories.some(cat => cat.id === categoryId);

      const newState = {
        ...state,
        working: {
          ...state.working,
          categories: state.working.categories.filter(cat => cat.id !== categoryId)
        },
        modifiedIds: {
          ...state.modifiedIds,
          categories: addToSet(state.modifiedIds.categories, categoryId)
        },
        deletedCategoryIds: existsInOriginal
          ? [...state.deletedCategoryIds, categoryId]
          : state.deletedCategoryIds
      };

      // Drop any pending edit overlay for the deleted row — the deletion is
      // tracked via deletedCategoryIds (for existing rows) and the row is gone
      // from working; an unsaved copied row simply disappears.
      if (newState.modifiedCategories[categoryId]) {
        const { [categoryId]: _removed, ...rest } = newState.modifiedCategories;
        newState.modifiedCategories = rest;
      }

      newState.hasUnsavedChanges = hasUnsavedChanges(newState);
      return newState;
    }

    case 'LOAD_DATA_SUCCESS_PAGINATED': {
      const { tab, data, pagination } = action.payload;

      if (tab === 'attribute') {
        const transformedAttributes = data.map((attr: any) => ({
          ...attr,
          id: attr.attribute,
          attributeValues: attr.attributeValues ? attr.attributeValues.map((attrVal: any, index: number) => ({
            ...attrVal,
            attribute: attr.attribute,
            id: attrVal.attribute_value || `${attr.attribute}_${index}_${Date.now()}`
          })) : []
        }));

        // Re-apply unsaved edits: if a freshly-loaded row was edited earlier
        // (and not yet saved), show the overlay version instead of the server
        // row so the user's review/mapped edits persist across search / filter
        // / pagination reloads. Display stays scoped to the current page — the
        // overlay only re-skins rows that are actually in this page.
        const mergedAttributes = transformedAttributes.map((attr: MappingAttribute) =>
          state.modifiedIds.attributes.has(attr.attribute) && state.modifiedAttributes[attr.attribute]
            ? { ...attr, ...state.modifiedAttributes[attr.attribute] }
            : attr
        );

        return {
          ...state,
          working: {
            ...state.working,
            attributes: mergedAttributes
          },
          original: {
            ...state.original,
            attributes: deepClone(transformedAttributes)
          },
          pagination: {
            ...state.pagination,
            attributes: {
              currentPage: pagination.current_page,
              hasMore: pagination.has_next,
              loading: false,
              totalItems: pagination.total_items,
              uniqueMappedCount: pagination.unique_mapped_count,
              pendingReviewCount: pagination.pending_review_count
            }
          }
        };
      } else if (tab === 'attributevalue') {
        // Phase 2: AV docs are keyed on `mapped_attribute` (the parent grouping
        // field). The legacy incoming `attribute` field is no longer set.
        const flattenedData = data.map((attrVal: any, index: number) => ({
          ...attrVal,
          id: attrVal.attribute_value || `${attrVal.mapped_attribute}_${index}_${Date.now()}`
        }));

        // Step 1: Get the parent (mapped) attribute name being loaded
        const loadedAttributeName = flattenedData.length > 0 ? flattenedData[0].mapped_attribute : null;

        // Step 2: Preserve modified attribute values from OTHER mapped-attributes in working.
        // Identity is the composite (mapped_attribute, attribute_value) key —
        // matching on attribute_value alone made another attribute's edit of
        // the same value string count as "modified" here, which then
        // substituted THAT attribute's row for this one's in Step 3 and hid
        // the freshly loaded rows entirely.
        const modifiedOtherAttributeValues = state.flattenedAttributeValues.working.filter(av =>
          state.modifiedIds.attributeValues.has(avRowKey(av.mapped_attribute, av.attribute_value)) &&
          av.mapped_attribute !== loadedAttributeName
        );

        // Step 3: For the loaded mapped attribute, prefer modified versions over fresh API data
        const mergedLoadedAttributeData = flattenedData.map((freshAV: MappingAttributeValue) => {
          const freshKey = avRowKey(freshAV.mapped_attribute, freshAV.attribute_value);
          const isModified = state.modifiedIds.attributeValues.has(freshKey);

          if (isModified) {
            const modifiedVersion = state.flattenedAttributeValues.working.find(
              av => avRowKey(av.mapped_attribute, av.attribute_value) === freshKey
            );
            return modifiedVersion || freshAV;
          }

          return freshAV;
        });

        const mergedWorkingValues = [
          ...modifiedOtherAttributeValues,
          ...mergedLoadedAttributeData
        ];

        const originalOtherAttributes = state.flattenedAttributeValues.original.filter(
          av => av.mapped_attribute !== loadedAttributeName
        );
        const mergedOriginalValues = [
          ...originalOtherAttributes,  // Keep original values from other attributes
          ...flattenedData             // Add fresh server state for loaded attribute
        ];

        return {
          ...state,
          flattenedAttributeValues: {
            working: mergedWorkingValues,   // ✅ Preserves unsaved changes
            original: mergedOriginalValues  // ✅ Maintains server state for all loaded attributes
          },
          pagination: {
            ...state.pagination,
            attributeValues: {
              currentPage: pagination.current_page,
              hasMore: pagination.has_next,
              loading: false,
              totalItems: pagination.total_items,
              // The data endpoint no longer returns unique_mapped_count (it's
              // fetched on its own endpoint via SET_AV_UNIQUE_COUNT). Preserve
              // any count already set rather than clobbering it with undefined.
              uniqueMappedCount: pagination.unique_mapped_count != null
                ? pagination.unique_mapped_count
                : state.pagination.attributeValues.uniqueMappedCount,
              uniqueMappedCountLoading: state.pagination.attributeValues.uniqueMappedCountLoading,
              pendingReviewCount: pagination.pending_review_count
            }
          }
        };
      } else if (tab === 'category') {
        const transformedCategories = data.map((cat: any, index: number) => ({
          ...cat,
          id: cat.id || `category_${index}_${Date.now()}`,
          // Ensure attribute is always an array
          attribute: Array.isArray(cat.attribute) ? cat.attribute : []
        }));

        // Re-apply unsaved edits onto matching fresh rows (so an edit re-appears
        // when its row reloads), and re-inject unsaved COPIED rows (carry
        // copy_of_id) that aren't in this page so they don't vanish. Display
        // otherwise stays the current server page.
        const loadedCatIds = new Set(transformedCategories.map((c: MappingCategory) => c.id));
        const mergedCategories = [
          ...Object.values(state.modifiedCategories).filter(
            (c) => c && (c as MappingCategory).copy_of_id && !loadedCatIds.has((c as MappingCategory).id)
          ) as MappingCategory[],
          ...transformedCategories.map((c: MappingCategory) =>
            state.modifiedIds.categories.has(c.id) && state.modifiedCategories[c.id]
              ? state.modifiedCategories[c.id]
              : c
          ),
        ];
        // Accumulate `original` across pages so hasMeaningfulChanges can still
        // find the server baseline for an edited row that's now off-page.
        const mergedOriginalCategories = [
          ...state.original.categories.filter((c) => !loadedCatIds.has(c.id)),
          ...deepClone(transformedCategories),
        ];

        return {
          ...state,
          working: {
            ...state.working,
            categories: mergedCategories
          },
          original: {
            ...state.original,
            categories: mergedOriginalCategories
          },
          pagination: {
            ...state.pagination,
            categories: {
              currentPage: pagination.current_page,
              hasMore: pagination.has_next,
              loading: false,
              totalItems: pagination.total_items,
              uniqueMappedCount: pagination.unique_mapped_count,
              pendingReviewCount: pagination.pending_review_count
            }
          }
        };
      }
      return state;
    }

    case 'APPEND_DATA': {
      const { tab, data } = action.payload;

      if (tab === 'attribute') {
        const transformedAttributes = data.map((attr: any) => ({
          ...attr,
          id: attr.attribute,
          attributeValues: attr.attributeValues ? attr.attributeValues.map((attrVal: any, index: number) => ({
            ...attrVal,
            attribute: attr.attribute,
            id: attrVal.attribute_value || `${attr.attribute}_${index}_${Date.now()}`
          })) : []
        }));

        // Create a Set of existing attribute IDs to prevent duplicates
        const existingIds = new Set(state.working.attributes.map((attr: MappingAttribute) => attr.attribute));
        const newAttributes = transformedAttributes
          .filter((attr: MappingAttribute) => !existingIds.has(attr.attribute))
          // Re-apply unsaved edits to appended rows (same as the paginated load)
          // so scrolling more in never resurfaces a stale, un-edited row.
          .map((attr: MappingAttribute) =>
            state.modifiedIds.attributes.has(attr.attribute) && state.modifiedAttributes[attr.attribute]
              ? { ...attr, ...state.modifiedAttributes[attr.attribute] }
              : attr
          );

        return {
          ...state,
          working: {
            ...state.working,
            attributes: [...state.working.attributes, ...newAttributes]
          }
        };
      } else if (tab === 'attributevalue') {
        const flattenedData = data.map((attrVal: any, index: number) => ({
          ...attrVal,
          id: attrVal.attribute_value || `${attrVal.attribute}_${index}_${Date.now()}`
        }));

        // Create a Set of existing attribute value keys to prevent duplicates.
        // Composite (mapped_attribute, attribute_value) key — the same value
        // string under a DIFFERENT mapped attribute is a distinct row and must
        // not be dropped as a "duplicate".
        const existingIds = new Set(state.flattenedAttributeValues.working.map((av: MappingAttributeValue) => avRowKey(av.mapped_attribute, av.attribute_value)));
        const newAttributeValues = flattenedData.filter((av: MappingAttributeValue) => !existingIds.has(avRowKey(av.mapped_attribute, av.attribute_value)));

        // Also update original to include the new server state (for pagination)
        const existingOriginalIds = new Set(state.flattenedAttributeValues.original.map((av: MappingAttributeValue) => avRowKey(av.mapped_attribute, av.attribute_value)));
        const newOriginalValues = flattenedData.filter((av: MappingAttributeValue) => !existingOriginalIds.has(avRowKey(av.mapped_attribute, av.attribute_value)));

        return {
          ...state,
          flattenedAttributeValues: {
            working: [...state.flattenedAttributeValues.working, ...newAttributeValues],
            original: [...state.flattenedAttributeValues.original, ...newOriginalValues]
          }
        };
      } else if (tab === 'category') {
        const transformedCategories = data.map((cat: any, index: number) => ({
          ...cat,
          id: cat.id || `category_${index}_${Date.now()}`,
          // Ensure attribute is always an array
          attribute: Array.isArray(cat.attribute) ? cat.attribute : []
        }));

        // Create a Set of existing category IDs to prevent duplicates
        const existingIds = new Set(state.working.categories.map((cat: MappingCategory) => cat.id));
        const newCategories = transformedCategories
          .filter((cat: MappingCategory) => !existingIds.has(cat.id))
          // Re-apply unsaved edits to appended rows so scrolling never
          // resurfaces a stale, un-edited row.
          .map((cat: MappingCategory) =>
            state.modifiedIds.categories.has(cat.id) && state.modifiedCategories[cat.id]
              ? state.modifiedCategories[cat.id]
              : cat
          );

        // Append originals for the new page so hasMeaningfulChanges keeps a
        // baseline for appended rows.
        const existingOriginalCatIds = new Set(state.original.categories.map((c) => c.id));
        const newOriginalCats = transformedCategories.filter((c: MappingCategory) => !existingOriginalCatIds.has(c.id));

        return {
          ...state,
          working: {
            ...state.working,
            categories: [...state.working.categories, ...newCategories]
          },
          original: {
            ...state.original,
            categories: [...state.original.categories, ...deepClone(newOriginalCats)]
          }
        };
      }
      return state;
    }

    case 'UPDATE_PAGINATION': {
      const { tab, pagination } = action.payload;

      // Map tab type to pagination key
      const getPaginationKey = (tab: MappingTabType): 'attributes' | 'attributeValues' | 'categories' => {
        switch (tab) {
          case 'attribute':
            return 'attributes';
          case 'attributevalue':
            return 'attributeValues';
          case 'category':
            return 'categories';
          default:
            return 'attributes';
        }
      };

      const paginationKey = getPaginationKey(tab);

      return {
        ...state,
        pagination: {
          ...state.pagination,
          [paginationKey]: {
            ...state.pagination[paginationKey],
            ...pagination
          }
        }
      };
    }

    case 'RESET_TAB_DATA': {
      const { tab } = action.payload;

      if (tab === 'attribute') {
        return {
          ...state,
          working: {
            ...state.working,
            attributes: []
          },
          original: {
            ...state.original,
            attributes: []
          },
          pagination: {
            ...state.pagination,
            attributes: {
              currentPage: 0,
              hasMore: true,
              loading: false,
              totalItems: 0,
              uniqueMappedCount: 0,
              pendingReviewCount: 0
            }
          }
        };
      } else if (tab === 'attributevalue') {
        return {
          ...state,
          flattenedAttributeValues: {
            working: [],
            original: []
          },
          pagination: {
            ...state.pagination,
            attributeValues: {
              currentPage: 0,
              hasMore: true,
              loading: false,
              totalItems: 0,
              uniqueMappedCount: 0,
              pendingReviewCount: 0
            }
          }
        };
      } else if (tab === 'category') {
        return {
          ...state,
          working: {
            ...state.working,
            categories: []
          },
          original: {
            ...state.original,
            categories: []
          },
          pagination: {
            ...state.pagination,
            categories: {
              currentPage: 0,
              hasMore: true,
              loading: false,
              totalItems: 0,
              uniqueMappedCount: 0,
              pendingReviewCount: 0
            }
          }
        };
      }
      return state;
    }

    default:
      return state;
  }
};